import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useFamilyGroupStore } from '../../store/familyGroupStore';
import { toast } from 'sonner';

interface CreateFamilyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateFamilyModal: React.FC<CreateFamilyModalProps> = ({ isOpen, onClose }) => {
  const [familyName, setFamilyName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const { createFamilyGroup, fetchFamilyGroups, setActiveFamilyId } = useFamilyGroupStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!familyName.trim()) {
      setErrors({ familyName: 'Por favor, introduce un nombre de familia' });
      return;
    }

    setLoading(true);

    try {
      const result = await createFamilyGroup(familyName.trim());

      if (result.success && result.familyGroupId) {
        toast.success('¡Grupo familiar creado correctamente!');
        setActiveFamilyId(result.familyGroupId);
        await fetchFamilyGroups();
        handleClose();
      } else {
        toast.error(result.error || 'Error al crear el grupo familiar');
      }
    } catch (error) {
      toast.error('Error al crear el grupo familiar');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFamilyName('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-stone-900">
              Crear Grupo Familiar
            </h2>
            <p className="text-sm text-stone-600 mt-1">
              Crea un nuevo grupo para organizar las historias de tu familia
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Nombre de la Familia <span className="text-rose-500">*</span>
            </label>
            <Input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="ej: Sotos Villaescusa"
              disabled={loading}
              maxLength={100}
              autoFocus
            />
            {errors.familyName && (
              <p className="mt-2 text-sm text-rose-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.familyName}
              </p>
            )}
            <p className="mt-2 text-xs text-stone-500">
              Puedes crear hasta 4 grupos familiares como propietario.
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              fullWidth
              className="sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={loading}
              fullWidth
              className="sm:w-auto"
            >
              Crear Grupo Familiar
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreateFamilyModal;
